/**
 * The AI transparency notice that sits under every Velo composer.
 *
 * This is a LEGAL disclosure, not decoration: a user must be able to tell that
 * the thing answering them is a machine, and that its answers can be wrong.
 * Until 2026-08-10 the string existed — translated into all 57 locales under
 * `aiAssistant.disclaimer` — and was rendered by nothing. The only surviving
 * hint that Velo is an AI was the tooltip on the launcher button, and tooltips
 * do not appear on touch devices, so on a tablet the panel showed a name and
 * nothing else.
 *
 * TWO sentences, and they are not interchangeable:
 *
 *   - `aiSystemNotice` — "you are interacting with an AI system". This is the
 *     one Art. 50(1) of Regulation (EU) 2024/1689 requires, in force since
 *     2026-08-02 and fineable under Art. 99(4)(g). It is owed regardless of
 *     what our own privacy policy says, which is why it cannot be softened or
 *     dropped the way a self-imposed promise could be.
 *   - `disclaimer` — "answers can be inaccurate". An accuracy warning. It was
 *     here first and reads like a disclosure, which is exactly why the gap went
 *     unnoticed for a month: a reviewer sees a notice under the composer and
 *     stops looking. It does NOT say the counterpart is a machine, so on its
 *     own it does not satisfy Art. 50(1).
 *
 * Deliberately ONE shared component rather than a line copied into each panel:
 * the four composers live in four files owned by four different flows, and a
 * wording or styling change that has to be made four times is a wording change
 * that will be made once.
 *
 * Not `aria-hidden`, and not a `title`/tooltip: a disclosure that assistive
 * technology cannot reach is not a disclosure. Plain text in the flow.
 *
 * Pinned by `__tests__/AiDisclaimer.test.tsx`, which mounts all four surfaces.
 * That test is the point — the previous gap was invisible precisely because
 * nothing asserted the notice was on screen.
 */
import { useTranslations } from "next-intl";

export function AiDisclaimer() {
  const t = useTranslations("aiAssistant");

  return (
    <p
      data-testid="ai-disclaimer"
      className="body-3-regular font-tertiary px-3 pb-2 pt-1 text-center text-balance"
    >
      <span data-testid="ai-system-notice">{t("aiSystemNotice")}</span>{" "}
      {t("disclaimer")}
    </p>
  );
}
